import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article, AccentTone } from '@/types/content';
import ArticleCard, { toneBadge } from '@/components/feature/ArticleCard';

export type CategoryLayout = 'grid' | 'list' | 'timeline' | 'grouped';

interface Props {
  articles: Article[];
  layout: CategoryLayout;
  accent: AccentTone;
}

export default function CategoryArticles({ articles, layout, accent }: Props) {
  const tags = useMemo(
    () => Array.from(new Set(articles.flatMap((a) => a.tags))),
    [articles],
  );
  const [activeTag, setActiveTag] = useState<string>('全部');

  const filtered = useMemo(
    () => (activeTag === '全部' ? articles : articles.filter((a) => a.tags.includes(activeTag))),
    [articles, activeTag],
  );

  const groups = useMemo(() => {
    const map = new Map<string, Article[]>();
    filtered.forEach((a) => {
      const key = a.tags[0] ?? '其他';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div>
      {/* 标签筛选 */}
      <div className="flex flex-wrap items-center gap-2 pb-6 mb-8 border-b border-background-200">
        <span className="text-xs text-foreground-500 mr-1">按标签筛选</span>
        {['全部', ...tags].map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => setActiveTag(tag)}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
              activeTag === tag
                ? `${toneBadge[accent]} font-medium`
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {tag}
          </button>
        ))}
        <span className="ml-auto text-xs text-foreground-400">共 {filtered.length} 篇</span>
      </div>

      {filtered.length === 0 && (
        <p className="py-12 text-center text-sm text-foreground-500">
          这个标签下暂时还没有内容，换个标签看看？
        </p>
      )}

      {layout === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {filtered.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      )}

      {layout === 'list' && (
        <div>
          {filtered.map((a) => (
            <ArticleCard key={a.slug} article={a} variant="list" />
          ))}
        </div>
      )}

      {layout === 'timeline' && (
        <ol className="relative border-l border-background-300 ml-2">
          {filtered.map((a) => (
            <li key={a.slug} className="relative pl-7 pb-9 last:pb-0">
              <span
                className={`absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-background-50 ${
                  accent === 'accent'
                    ? 'bg-accent-500'
                    : accent === 'primary'
                      ? 'bg-primary-500'
                      : 'bg-secondary-500'
                }`}
              />
              <time className="text-xs text-foreground-500" dateTime={a.publishedAt}>
                {a.publishedAt}
              </time>
              <h3 className="mt-1.5 font-heading text-lg font-semibold leading-snug">
                <Link
                  to={`/article/${a.slug}`}
                  className="text-foreground-950 hover:text-primary-600 transition-colors"
                >
                  {a.title}
                </Link>
              </h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600 line-clamp-2">{a.excerpt}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {a.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded-full bg-background-100 text-[11px] text-foreground-600"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ol>
      )}

      {layout === 'grouped' && (
        <div className="space-y-12">
          {groups.map(([tag, items]) => (
            <section key={tag}>
              <div className="flex items-center gap-3 mb-5">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${toneBadge[accent]}`}>
                  #{tag}
                </span>
                <span className="flex-1 h-px bg-background-200" />
                <span className="text-xs text-foreground-400">{items.length} 篇</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {items.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}